import yaml from 'js-yaml';
import partnerService from '../services/partnerService';

/**
 * 清理值，確保所有值都是 React 可渲染的類型
 * @param {*} value - 要清理的值
 * @returns {string|number|boolean|Array} - 清理後的值
 */
export const cleanValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value;
    }
    // 對象類型轉為空字符串
    return '';
  }
  return String(value);
};

/**
 * 清理標籤數組
 * @param {Array} tags - 標籤數組
 * @returns {Array<string>} - 清理後的字符串數組
 */
export const cleanTags = (tags) => {
  if (!Array.isArray(tags)) return [];
  
  return tags
    .filter(tag => tag !== null && tag !== undefined)
    .map(tag => {
      if (typeof tag === 'string') return tag;
      if (typeof tag === 'object') return '';
      return String(tag);
    })
    .filter(tag => tag !== '');
};

/**
 * 驗證並清理節點數據
 * @param {Object} node - 節點對象
 * @param {number} index - 節點索引
 * @returns {Object} - 清理後的節點對象
 */
export const cleanNode = (node, index) => {
  return {
    id: cleanValue(node.id) || `node-${Date.now()}-${index}`,
    title: cleanValue(node.title) || '',
    content: cleanValue(node.content) || '',
    tags: cleanTags(node.tags)
  };
};

/**
 * 驗證並清理群組數據
 * @param {Object} group - 群組對象
 * @param {number} index - 群組索引
 * @returns {Object} - 清理後的群組對象
 */
export const cleanGroup = (group, index) => {
  return {
    id: cleanValue(group.id) || `group-${Date.now()}-${index}`,
    name: cleanValue(group.name) || '未命名群組',
    timing: cleanValue(group.timing) || 'immediate',
    description: cleanValue(group.description) || '',
    isDefault: Boolean(group.isDefault),
    nodes: Array.isArray(group.nodes) ? group.nodes.map((node, nIndex) => cleanNode(node, nIndex)) : []
  };
};

/**
 * 清理設定數據
 * @param {Object} settings - 設定對象
 * @returns {Object} - 清理後的設定對象
 */
export const cleanSettings = (settings) => {
  if (!settings || typeof settings !== 'object') return {};
  
  return {
    ...settings,
    chatmode: cleanValue(settings.chatmode) || 'sticker'
  };
};

/**
 * 驗證並清理導入的群組數據
 * @param {Array} groups - 群組數組
 * @returns {Array} - 清理後的群組數組
 */
export const validateAndCleanGroups = (groups) => {
  if (!Array.isArray(groups)) return [];
  
  return groups.map((group, index) => cleanGroup(group, index));
};

/**
 * 獲取夥伴的完整配置
 * @param {Object} partner - 活動夥伴對象
 * @param {string} currentUserId - 當前用戶 ID
 * @returns {Promise<Object>} - 夥伴配置對象
 */
export const getPartnerFullConfig = async (partner, currentUserId) => {
  try {
    const partnerDetail = await partnerService.getPartner(partner.partnerId);
    
    // 檢查夥伴是否屬於當前用戶
    if (partnerDetail.userId === currentUserId) {
      // 導出完整配置
      return {
        name: partnerDetail.name,
        description: partnerDetail.description,
        category: partnerDetail.category,
        tags: partnerDetail.tags,
        roleTask: partnerDetail.roleTask,
        knowledgeBase: partnerDetail.knowledgeBase,
        communicationStyle: partnerDetail.communicationStyle,
        responseTimeline: partnerDetail.responseTimeline,
        isPublicTemplate: partnerDetail.isPublicTemplate || false,
        groupId: partner.groupId || null,
        activityConfig: {
          partnerId: partner.partnerId,
          addedAt: partner.addedAt
        }
      };
    } else {
      // 非用戶擁有的夥伴，只導出基本引用信息
      return {
        name: partner.partnerName,
        partnerId: partner.partnerId,
        groupId: partner.groupId,
        isExternal: true,
        note: '此夥伴不屬於當前用戶，導入時需要手動從夥伴市場重新添加'
      };
    }
  } catch (error) {
    console.error(`獲取夥伴 ${partner.partnerId} 配置失敗:`, error);
    // 如果獲取失敗，只導出基本信息
    return {
      name: partner.partnerName,
      partnerId: partner.partnerId,
      groupId: partner.groupId,
      isExternal: true,
      note: '無法獲取夥伴完整配置，可能是權限問題或夥伴已被刪除'
    };
  }
};

/**
 * 構建導出數據
 * @param {Object} params - 參數對象
 * @param {Object} params.activityInfo - 活動資訊
 * @param {Object} params.roomSetting - 房間設定
 * @param {Array} params.nodeGroups - 節點群組
 * @param {Array} params.activityPartners - 活動夥伴列表
 * @param {string} params.currentUserId - 當前用戶 ID
 * @returns {Promise<Object>} - 導出數據對象
 */
export const buildExportData = async ({ activityInfo, roomSetting, nodeGroups, activityPartners, currentUserId }) => {
  // 獲取完整的夥伴配置
  const partnersWithFullConfig = await Promise.all(
    activityPartners.map(partner => getPartnerFullConfig(partner, currentUserId))
  );

  return {
    version: '1.0',
    exportDate: new Date().toISOString(),
    activityInfo: {
      title: activityInfo.title,
      content: activityInfo.content,
    },
    settings: {
      chatmode: roomSetting?.chatmode,
      initialNodeGroups: nodeGroups,
      ...roomSetting
    },
    partners: partnersWithFullConfig
  };
};

/**
 * 將數據導出為 YAML 格式
 * @param {Object} data - 要導出的數據
 * @returns {string} - YAML 字符串
 */
export const exportToYAML = (data) => {
  return yaml.dump(data, {
    indent: 2,
    lineWidth: -1,
    noRefs: true,
    sortKeys: false
  });
};

/**
 * 從 YAML/JSON 字符串解析數據
 * @param {string} content - 文件內容
 * @param {string} fileName - 文件名
 * @returns {Object} - 解析後的數據
 */
export const parseImportData = (content, fileName) => {
  if (fileName.endsWith('.yaml') || fileName.endsWith('.yml')) {
    return yaml.load(content);
  } else if (fileName.endsWith('.json')) {
    return JSON.parse(content);
  } else {
    // 嘗試解析
    try {
      return JSON.parse(content);
    } catch {
      return yaml.load(content);
    }
  }
};

/**
 * 驗證導入數據格式
 * @param {Object} data - 導入的數據
 * @throws {Error} - 如果數據格式無效
 */
export const validateImportData = (data) => {
  if (!data || typeof data !== 'object') {
    throw new Error('解析失敗：無效的文件內容');
  }
  
  if (!data.version) {
    throw new Error('無效的設定檔案格式：缺少 version 欄位');
  }
  
  if (!data.settings) {
    throw new Error('無效的設定檔案格式：缺少 settings 欄位');
  }
};

/**
 * 分析導入數據中的夥伴信息
 * @param {Array} partners - 夥伴數組
 * @returns {Object} - 分析結果
 */
export const analyzePartners = (partners) => {
  let ownedPartnersCount = 0;
  let externalPartnersCount = 0;
  let partnersWithFullConfig = [];
  
  if (partners && Array.isArray(partners)) {
    partners.forEach(p => {
      if (p.isExternal) {
        externalPartnersCount++;
      } else if (p.roleTask && p.knowledgeBase && p.communicationStyle) {
        ownedPartnersCount++;
        partnersWithFullConfig.push(p.name);
      }
    });
  }
  
  return {
    ownedPartnersCount,
    externalPartnersCount,
    partnersWithFullConfig
  };
};

/**
 * 構建導入確認消息
 * @param {Object} importData - 導入的數據
 * @param {Object} partnerAnalysis - 夥伴分析結果
 * @returns {string} - 確認消息
 */
export const buildImportConfirmMessage = (importData, partnerAnalysis) => {
  const { ownedPartnersCount, externalPartnersCount, partnersWithFullConfig } = partnerAnalysis;
  
  let message = `確定要導入設定嗎？\n\n` +
    `導出日期：${importData.exportDate ? new Date(importData.exportDate).toLocaleString('zh-TW') : '未知'}\n` +
    `活動標題：${importData.activityInfo?.title || '未知'}\n` +
    `引導單元群組數：${importData.settings?.initialNodeGroups?.length || 0}\n` +
    `AI 夥伴數：${importData.partners?.length || 0}`;
  
  if (ownedPartnersCount > 0) {
    message += `\n  - 包含完整配置的夥伴：${ownedPartnersCount} 個`;
    message += `\n    (${partnersWithFullConfig.join('、')})`;
  }
  if (externalPartnersCount > 0) {
    message += `\n  - 外部夥伴引用：${externalPartnersCount} 個`;
  }
  
  message += `\n\n注意：導入將覆蓋當前設定（但不會立即保存）`;
  
  if (ownedPartnersCount > 0) {
    message += `\n包含完整配置的夥伴將在保存時自動創建並添加到活動中。`;
  }
  
  return message;
};

/**
 * 構建導入成功消息
 * @param {Object} partnerAnalysis - 夥伴分析結果
 * @returns {string} - 成功消息
 */
export const buildImportSuccessMessage = (partnerAnalysis) => {
  const { ownedPartnersCount, externalPartnersCount } = partnerAnalysis;
  
  let message = '設定已導入';
  if (ownedPartnersCount > 0) {
    message += `\n包含 ${ownedPartnersCount} 個完整配置的夥伴`;
    message += `\n保存設定後，這些夥伴將自動創建並添加到活動中`;
  }
  if (externalPartnersCount > 0) {
    message += `\n${externalPartnersCount} 個外部夥伴需要手動從夥伴市場重新添加`;
  }
  
  return message;
};

/**
 * 創建並下載 YAML 文件
 * @param {string} yamlContent - YAML 內容
 * @param {string} fileName - 文件名
 */
export const downloadYAMLFile = (yamlContent, fileName) => {
  const blob = new Blob([yamlContent], { type: 'text/yaml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
